import { useId } from 'react';
import { Input } from '../../ui';
import type { DirectoryPerson } from '../../features/teams/types';

export interface PeopleInputProps {
  /** Current alias value. */
  value: string;
  /** Called with the new alias on change. */
  onChange: (value: string) => void;
  /** The directory people to suggest (from usePeopleDirectory / useTeams). */
  people: DirectoryPerson[];
  label?: string;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  /** Aliases to exclude from suggestions (e.g. already-added members). */
  exclude?: string[];
}

/**
 * A people picker: an alias text input augmented with a native `<datalist>` of
 * suggestions sourced from the team directory (aliases + names). Falls back to
 * plain free-text entry when the directory is empty, so it is a safe drop-in for
 * any existing alias field. Keeps the alias as the stored value.
 */
export function PeopleInput({
  value,
  onChange,
  people,
  label,
  placeholder = 'alias…',
  hint,
  disabled,
  exclude = [],
}: PeopleInputProps) {
  const listId = useId();
  const excluded = new Set(exclude.map((a) => a.toLowerCase()));
  const suggestions = people.filter(
    (p) => p.alias && !excluded.has(p.alias.toLowerCase()),
  );

  return (
    <>
      <Input
        label={label}
        placeholder={placeholder}
        hint={hint}
        value={value}
        disabled={disabled}
        list={suggestions.length ? listId : undefined}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((p) => (
            <option key={p.alias} value={p.alias}>
              {p.name ? `${p.name}${p.role ? ` · ${p.role}` : ''}` : p.alias}
            </option>
          ))}
        </datalist>
      )}
    </>
  );
}

export default PeopleInput;
